import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import { ToastContainer, toast } from 'react-toastify';
import { Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import countryLookup from '../utils/countryLookup';
import 'react-toastify/dist/ReactToastify.css';
import './Settings.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function AddOrEditStore() {
  const { token } = useContext(AuthContext);
  const { stores, fetchStores } = useContext(StoreContext);
  const { storeId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!storeId;

  const [storeForm, setStoreForm] = useState({
    name: '', 
    company_name: '',
    description: '', 
    handle: '', 
    is_cave: true,
    domain: '',
    domain_status: '',
    created_at: null,
    updated_at: null,
    logo: null, 
    background_image: null,
    google_app_email: '', 
    google_app_password: '', 
    send_email_as: '', 
    email_signature: '',
    policy: '',
    promo_message: '',
    enable_promo_message: false,
    promo_image: null,
    enable_promo_image: false,
    stripe_account_id: '',
    shipping_providers: {
      sendle: { enabled: false, api_key: '' },
      chitchats: { enabled: false, api_key: '' },
    },
    address: {
      line1: '',
      line2: '',
      suburb: '',
      postcode: '',
      state: '',
      country: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staticMapUrl, setStaticMapUrl] = useState("");
  const [stripeAccounts, setStripeAccounts] = useState([]);

  useEffect(() => {
    if (token) {
      fetchStripeAccounts();
    }
  }, [token]);

  useEffect(() => {
    if (token && isEdit && storeId) {
      const store = stores.find(s => s._id === storeId);
      if (store) {
        setStoreForm({
          _id: store._id,
          name: store.name || '',
          company_name: store.company_name || '',
          description: store.description || '',
          handle: store.handle || '',
          is_cave: store.is_cave,
          domain: store.domain || '',
          domain_status: store.domain_status || '',
          created_at: store.created_at || null,
          updated_at: store.updated_at || null,
          logo: store.logo || null,
          background_image: store.background_image || null,
          promo_image: store.promo_image || null, 
          enable_promo_image: store.enable_promo_image || false,
          promo_message: store.promo_message || '',
          enable_promo_message: store.enable_promo_message || false,
          google_app_email: store.google_app_email || '',
          google_app_password: store.google_app_password || '',
          send_email_as: store.send_email_as || '',
          email_signature: store.email_signature || '',
          policy: store.policy || '',
          stripe_account_id: store.stripe_account_id || '',
          shipping_providers: {
            sendle: {
              enabled: store.shipping_providers?.sendle?.enabled || false,
              api_key: store.shipping_providers?.sendle?.api_key || '',
            },
            chitchats: {
              enabled: store.shipping_providers?.chitchats?.enabled || false,
              api_key: store.shipping_providers?.chitchats?.api_key || '',
            },
          },
          address: {
            line1: store.address?.line1 || '',
            line2: store.address?.line2 || '',
            suburb: store.address?.suburb || '',
            postcode: store.address?.postcode || '',
            state: store.address?.state || '',
            country: store.address?.country || '',
          },
        });

        if (
          store.address &&
          store.address.suburb &&
          store.address.state &&
          store.address.country
        ) {
          const url = getStaticMapUrl(store.address);
          setStaticMapUrl(url);
        }
      }
    }
  }, [token, isEdit, storeId, stores]);

  const fetchStripeAccounts = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error fetching settings.');
      }

      const data = await response.json();
      if (data.stripe_accounts && data.stripe_accounts.length > 0) {
        setStripeAccounts(data.stripe_accounts);
      }
    } catch (error) {
      console.error('Error fetching Stripe accounts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let logoUrl = storeForm.logo;
    let backgroundUrl = storeForm.background_image;
    let promoUrl = storeForm.promo_image;
  
    try {
      // Upload each asset if it's a new file
      if (isEdit) {
        if (storeForm.logo instanceof File) {
          const uploadedLogo = await uploadStoreAsset(storeForm._id, storeForm.logo, 'logo');
          if (uploadedLogo) logoUrl = uploadedLogo.file_url;
        }
    
        if (storeForm.background_image instanceof File) {
          const uploadedBackground = await uploadStoreAsset(storeForm._id, storeForm.background_image, 'background');
          if (uploadedBackground) backgroundUrl = uploadedBackground.file_url;
        }
    
        if (storeForm.promo_image instanceof File) {
          const uploadedPromo = await uploadStoreAsset(storeForm._id, storeForm.promo_image, 'promo');
          if (uploadedPromo) promoUrl = uploadedPromo.file_url;
        }
      }

      const storeData = {
        name: storeForm.name,
        company_name: storeForm.company_name,
        description: storeForm.description,
        handle: storeForm.handle,
        domain: storeForm.domain,
        is_cave: storeForm.is_cave,
        domain_status: storeForm.domain_status,
        logo: logoUrl,
        background_image: backgroundUrl,
        promo_image: promoUrl,
        enable_promo_image: storeForm.enable_promo_image || false,
        promo_message: storeForm.promo_message || '',
        enable_promo_message: storeForm.enable_promo_message || false,
        stripe_account_id: storeForm.stripe_account_id || null,
        google_app_email: storeForm.google_app_email || '', 
        google_app_password: storeForm.google_app_password || '',
        send_email_as: storeForm.send_email_as || '',
        email_signature: storeForm.email_signature || '',
        policy: storeForm.policy || '',
        shipping_providers: storeForm.shipping_providers && Object.keys(storeForm.shipping_providers).reduce((acc, providerKey) => {
          acc[providerKey] = {
            enabled: storeForm.shipping_providers[providerKey].enabled,
            api_key: storeForm.shipping_providers[providerKey].api_key || '',
          };
          return acc;
        }, {}),
        address: storeForm.address && {
          line1: storeForm.address.line1,
          line2: storeForm.address.line2,
          suburb: storeForm.address.suburb,
          postcode: storeForm.address.postcode,
          state: storeForm.address.state,
          country: storeForm.address.country,
        },
      };

      const url = isEdit ? `${STORE_API_URL}/stores/${storeId}` : `${STORE_API_URL}/stores`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(storeData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Error ${isEdit ? 'updating' : 'creating'} store.`);
      }

      // Show success message
      const successMessage = isEdit 
        ? `${storeForm.is_cave ? 'Cave' : 'Store'} "${storeForm.name}" has been updated successfully!`
        : `${storeForm.is_cave ? 'Cave' : 'Store'} "${storeForm.name}" has been created successfully!`;
      
      toast.success(successMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (error) {
      toast.error(error.message || 'An error occurred. Please try again.', {
        position: "top-center",
        autoClose: 5000,
      });
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreFormChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setStoreForm((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    }
    // Handle nested fields like address
    else if (name.startsWith('address_')) {
      const addressField = name.split('_')[1];
      setStoreForm((prevState) => ({
        ...prevState,
        address: {
          ...prevState.address,
          [addressField]: value,
        },
      }));
    } else {
      setStoreForm({ ...storeForm, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
  
    setStoreForm((prevForm) => ({
      ...prevForm,
      [name]: file,
    }));
  };

  const uploadStoreAsset = async (storeId, file, type) => {
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const response = await fetch(`${STORE_API_URL}/upload/${storeId}/assets/${type}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || `Error uploading store ${type}.`);
      }
  
      return data;
    } catch (error) {
      toast.error(error.message || `An error occurred during ${type} upload.`);
    }
  };

  const handlePreview = () => {
    const { suburb, postcode, state, country } = storeForm.address;
    if (suburb && postcode && state && country) {
      const url = getStaticMapUrl(storeForm.address);
      setStaticMapUrl(url);
    } else {
      alert("Please complete all address fields to preview the location.");
    }
  };

  const getStaticMapUrl = (address) => {
    const { suburb, state, country } = address;
    const addressQuery = `${suburb}, ${state}, ${country}`;
    const size = "800x200";
    const zoom = 12;
    const scale = 2;
    const marker = `markers=color:0xf5a623|label:Pickup`
  
    const style = [
      "feature:all|element:labels.text.fill|gamma:0.01|lightness:20|weight:1.39|color:0xffffff",
      "feature:all|element:labels.text.stroke|weight:0.96|saturation:9|visibility:on|color:0x000000",
      "feature:all|element:labels.icon|visibility:off",
      "feature:all|element:geometry|color:0x431C53",
      "feature:road|element:geometry|color:0xff4c4c",
      "feature:road|element:geometry.stroke|color:0xea3377",
      "feature:water|element:geometry|color:0xff4c4c",
      "feature:landscape|element:geometry|color:0x431C53",
      "feature:poi|element:geometry|color:0x8d1642",
      "feature:poi.park|element:geometry|lightness:20|saturation:-20",
    ].join("&style=");

    return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
      addressQuery
    )}&zoom=${zoom}&size=${size}&scale=${scale}&maptype=roadmap&${marker}|${encodeURIComponent(
      addressQuery
    )}&style=${style}&key=${GOOGLE_MAPS_API_KEY}`;
  };

  const handleShippingProviderChange = (e) => {
    const { name, checked } = e.target;
    setStoreForm((prevState) => ({
      ...prevState,
      shipping_providers: {
        ...prevState.shipping_providers,
        [name]: {
          ...prevState.shipping_providers[name],
          enabled: checked,
        },
      },
    }));
  };

  const handleShippingProviderApiKeyChange = (e, provider) => {
    const { value } = e.target;
    setStoreForm((prevState) => ({
      ...prevState,
      shipping_providers: {
        ...prevState.shipping_providers,
        [provider]: {
          ...prevState.shipping_providers[provider],
          api_key: value,
        },
      },
    }));
  };

  const handleSendTestEmail = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/settings/send-test-email/${storeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send test email');
      }

      const data = await response.json();
      toast.success(data.message || 'Test email sent successfully!');
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const initiateGmailAuthorization = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google/gmail`;
  };

  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <h2 className="underline mb-0">
            {isEdit ? `Edit ${storeForm.is_cave ? 'Cave' : 'Store'}` : `Create New ${storeForm.is_cave ? 'Cave' : 'Store'}`}
          </h2>
        </div>
        <div className="d-flex align-items-center">
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => navigate('/settings')}
            style={{ textDecoration: 'none' }}
          >
            <FaArrowLeft /> Settings
          </button>
        </div>
      </div>
      {isEdit && (
        <small className="text-muted">
          {storeForm.created_at ? 'Created: ' + new Date(storeForm.created_at).toLocaleString() : null}
          {storeForm.updated_at ? ' | Updated: ' + new Date(storeForm.updated_at).toLocaleString() : null}
        </small>
      )}
      <form onSubmit={handleSubmit}>
        <h5 className="headline">General Settings</h5>
        {isEdit && (
          <div className="form-group">
            <label>{storeForm.is_cave ? 'Cave' : 'Store'} ID:</label>
            <input
              type="text"
              name="storeForm._id"
              className="form-control"
              value={storeForm._id}
              onChange={handleStoreFormChange}
              required
              disabled
            />
          </div>
        )}
        <div className="form-group">
          <label>{storeForm.is_cave ? 'Cave' : 'Store'} Name:</label>
          <input
            type="text"
            name="name"
            placeholder="e.g. John's Digital Basement"
            className="form-control"
            value={storeForm.name || ''}
            onChange={handleStoreFormChange}
            required
          />
        </div>
        <div className="form-group">
          <label>{storeForm.is_cave ? 'Cave' : 'Store'} Description:</label>
          <input
            type="text"
            name="description"
            className="form-control"
            value={storeForm.description || ''}
            onChange={handleStoreFormChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Handle (must be at least 6 alphabetic characters):</label>
          <input
            type="text"
            name="handle"
            className="form-control"
            value={storeForm.handle || ''}
            onChange={handleStoreFormChange}
            required
          />
        </div>
        <div className="form-group">
          <label>{storeForm.is_cave ? 'Cave' : 'Store'} Domain (Optional):</label>
          <input
            type="text"
            name="domain"
            className="form-control"
            value={storeForm.domain || ''}
            onChange={handleStoreFormChange}
          />
        </div>

        {!storeForm.is_cave && (
          <>
            <h5 className="headline">Branding</h5>
            <div className="form-group">
              <label>Company Name:</label>
              <input
                type="text"
                name="company_name"
                className="form-control"
                placeholder="optional"
                value={storeForm.company_name || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>Store Logo:</label>
              <input
                type="file"
                name="logo"
                className="form-control"
                onChange={handleFileChange}
              />
            </div>
            <div className="form-group">
              <label>Store Background:</label>
              <input
                type="file"
                name="background_image"
                className="form-control"
                onChange={handleFileChange}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="enable_promo_message"
                  className="form-check-input me-2"
                  checked={storeForm.enable_promo_message || false}
                  onChange={handleStoreFormChange}
                />
                Store Promo Message:
              </label>
              <textarea
                name="promo_message"
                className="form-control"
                value={storeForm.promo_message || ''}
                onChange={handleStoreFormChange}
                rows="3"
                placeholder="Enter your promo message here"
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="enable_promo_image"
                  className="form-check-input me-2"
                  checked={storeForm.enable_promo_image || false}
                  onChange={handleStoreFormChange}
                />
                Promo Image:
              </label>
              <input
                type="file"
                name="promo_image"
                className="form-control"
                onChange={handleFileChange}
              />
            </div>

            <h5 className="headline">Legal</h5>
            <p>
              The store policy is a crucial element for setting clear expectations with your customers. Use this section to outline 
              key details such as whether sales are final, if local pickups are allowed, how returns are handled, and your approach 
              to disputes. Keep in mind that Sunthar.com does not mediate between you and your customers.
            </p>
            <div className="form-group">
              <label>Store Policy</label>
              <textarea
                name="policy"
                className="form-control"
                value={storeForm.policy || ''}
                onChange={handleStoreFormChange}
                rows="4"
              />
            </div>

            <h5 className="headline">Store Address</h5>
            <p>Note: Please enter your exact address below. This will be used for handling shipping. For local pickup, only your approximate location based on your city will be publicly displayed. Buyers will contact you to obtain the exact address.</p>
            <div className="form-group">
              <label>Address (Line 1)</label>
              <input
                type="text"
                name="address_line1"
                className="form-control"
                placeholder="123 Phosphor Street"
                value={storeForm.address?.line1 || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>Address (Line 2)</label>
              <input
                type="text"
                name="address_line2"
                placeholder="Apt #123 (optional)"
                className="form-control"
                value={storeForm.address?.line2 || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>Suburb/City:</label>
              <input
                type="text"
                name="address_suburb"
                className="form-control"
                value={storeForm.address?.suburb || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>Postal/Zip Code:</label>
              <input
                type="text"
                name="address_postcode"
                className="form-control"
                value={storeForm.address?.postcode || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>State:</label>
              <input
                type="text"
                name="address_state"
                className="form-control"
                placeholder="NY"
                value={storeForm.address?.state || ''}
                onChange={handleStoreFormChange}
              />
            </div>
            <div className="form-group">
              <label>Country:</label>
              <select
                className="form-select"
                name="address_country"
                value={storeForm.address?.country || ''}
                onChange={handleStoreFormChange}
              >
                <option value="">Select Country</option>
                {Object.entries(countryLookup).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label></label>
              <div style={{width:'100%'}}>
                <button type="button" onClick={handlePreview} className="btn btn-sm btn-primary me-2">
                  Preview Location
                </button>
              </div>
            </div>
            {staticMapUrl && (
              <div className="form-group mt-4">
                <label>Location Preview:</label>
                <div style={{width:'100%'}}>
                  <img
                    src={staticMapUrl}
                    alt="Approximate Location"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
              </div>
            )}

            <h5 className="headline">Email Settings</h5>
            <p>Note: We will request permission to send emails on your behalf using your Gmail account. We will not have access to your inbox or any other email content. For free Gmail accounts, 
              there is a limit of 500 emails per 24 hours, which should be sufficient for hobbyist stores or small-scale operations.</p>
            <div className="form-group">
              <label></label>
              <div style={{width:'100%'}}>
                <button 
                  type="button" 
                  onClick={initiateGmailAuthorization} 
                  className="btn btn-sm btn-primary me-2"
                >
                  Authorize Gmail For Email Sending
                </button>
                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  className="btn btn-sm btn-primary"
                >
                  Send Test Email
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Email Signature</label>
              <textarea
                name="email_signature"
                className="form-control"
                value={storeForm.email_signature || ''}
                onChange={handleStoreFormChange}
                rows="4"
              />
            </div>
            <p><span style={{color: '#f00'}}>Note:</span> to use <span style={{color: '#ff0'}}>Send mail as</span> feature, you must make sure your email is already onboarded in the <span style={{color: '#ff0'}}>Accounts and Import</span> section of your Gmail Settings page.
            Otherwise, please do not use this feature. If your emails fail to send frequently, email feature will be permanently disabled for your account.</p>
            <div className="form-group">
              <label>Send mail as</label>
              <input
                type="text"
                name="send_email_as"
                className="form-control"
                value={storeForm.send_email_as || ''}
                onChange={handleStoreFormChange}
              />
            </div>

            <h5 className="headline">Shipping Settings</h5>
            <p>Note: ChitChats is only available in Canada. Do not enable this, if you are elsewhere. Sendle currently works both in Canada and the US.</p>
            <div className="form-group">
              <label>Shipping Providers to Enable?</label>
              <div className="checkbox-group">
                {Object.keys(storeForm.shipping_providers).map((providerKey) => (
                  <div key={providerKey} className="checkbox-item">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      name={providerKey}
                      checked={storeForm.shipping_providers[providerKey].enabled}
                      onChange={handleShippingProviderChange}
                    />
                    {providerKey.charAt(0).toUpperCase() + providerKey.slice(1)}
                    {storeForm.shipping_providers[providerKey].enabled && (
                      <div className="provider-settings">
                        <div className="form-group mt-2">
                          <label>{providerKey.charAt(0).toUpperCase() + providerKey.slice(1)} API Key</label>
                          <input
                            type="text"
                            className="form-control"
                            value={storeForm.shipping_providers[providerKey].api_key}
                            onChange={(e) => handleShippingProviderApiKeyChange(e, providerKey)}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mt-4">
          <button 
            type="submit" 
            className="btn btn-primary me-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? `Update ${storeForm.is_cave ? 'Cave' : 'Store'}` : `Create ${storeForm.is_cave ? 'Cave' : 'Store'}`
            )}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => navigate('/settings')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </Container>
  );
}

export default AddOrEditStore; 